/**
 * @description
 * Manual Invoice Creation Modal Component
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from 'react-hot-toast';
import type { ManualInvoiceModalProps } from './types';

export function ManualInvoiceModal({ clients, subscriptions, onClose, onSuccess }: ManualInvoiceModalProps) {
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [invoiceType, setInvoiceType] = useState<'custom' | 'subscription'>('custom');
  const [selectedSubscription, setSelectedSubscription] = useState<string>('');
  const [customItems, setCustomItems] = useState<Array<{ description: string; amount: number; quantity: number }>>([
    { description: '', amount: 0, quantity: 1 }
  ]);
  const [loading, setLoading] = useState(false);

  const addCustomItem = () => {
    setCustomItems([...customItems, { description: '', amount: 0, quantity: 1 }]);
  };

  const removeCustomItem = (index: number) => {
    setCustomItems(customItems.filter((_, i) => i !== index));
  };

  const updateCustomItem = (index: number, field: string, value: string | number) => {
    setCustomItems(customItems.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const calculateTotal = () => {
    if (invoiceType === 'subscription' && selectedSubscription) {
      const subscription = subscriptions.find(s => s.id === selectedSubscription);
      return subscription ? subscription.amount : 0;
    }
    return customItems.reduce((total, item) => total + (item.amount * item.quantity), 0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  const handleCreateInvoice = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    try {
      setLoading(true);
      
      if (invoiceType === 'subscription') {
        if (!selectedSubscription) {
          toast.error('Please select a subscription');
          return;
        }
        await api.createSubscriptionInvoice(selectedClient, selectedSubscription);
      } else {
        const validItems = customItems.filter(item => item.description && item.amount > 0);
        if (validItems.length === 0) {
          toast.error('Please add at least one item');
          return;
        }
        await api.createManualInvoice(selectedClient, validItems);
      }
      
      toast.success('Invoice created successfully');
      onSuccess();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg border p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Create Manual Invoice</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Client Selection */}
          <div>
            <label className="text-sm font-medium">Client</label>
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select a client</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          {/* Invoice Type */}
          <div>
            <label className="text-sm font-medium">Invoice Type</label>
            <div className="mt-2 flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="custom"
                  checked={invoiceType === 'custom'}
                  onChange={(e) => setInvoiceType(e.target.value as 'custom' | 'subscription')}
                  className="mr-2"
                />
                Custom Items
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="subscription"
                  checked={invoiceType === 'subscription'}
                  onChange={(e) => setInvoiceType(e.target.value as 'custom' | 'subscription')}
                  className="mr-2"
                />
                Subscription
              </label>
            </div>
          </div>

          {/* Subscription Selection */}
          {invoiceType === 'subscription' && (
            <div>
              <label className="text-sm font-medium">Subscription</label>
              <select
                value={selectedSubscription}
                onChange={(e) => setSelectedSubscription(e.target.value)}
                className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select a subscription</option>
                {subscriptions.map(subscription => (
                  <option key={subscription.id} value={subscription.id}>
                    {subscription.name} - {formatCurrency(subscription.amount)}/{subscription.interval}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Custom Items */}
          {invoiceType === 'custom' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Items</label>
                <button
                  onClick={addCustomItem}
                  className="text-sm text-primary hover:underline"
                >
                  + Add Item
                </button>
              </div>
              <div className="space-y-3">
                {customItems.map((item, index) => (
                  <div key={index} className="flex gap-3 items-end">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateCustomItem(index, 'description', e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div className="w-24">
                      <input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateCustomItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div className="w-32">
                      <input
                        type="number"
                        placeholder="Amount ($)"
                        value={item.amount}
                        onChange={(e) => updateCustomItem(index, 'amount', parseFloat(e.target.value) || 0)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <button
                      onClick={() => removeCustomItem(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total */}
          <div className="border-t pt-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold">Total:</span>
              <span className="text-lg font-semibold">{formatCurrency(calculateTotal())}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateInvoice}
              disabled={loading}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
